import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const NATURES = { verification: "Vérification", reparation: "Réparation", remplacement: "Remplacement", entretien: "Entretien", autre: "Autre" };
const blank = (eqIds) => ({ nature: "verification", title: "", planned_start: "", all_day: false, start_time: "08:00", planned_end: "", end_time: "", responsible_id: "", planning_note: "", equipment_ids: eqIds });

export function InterventionDialog({ open, onOpenChange, siteId, equipment, preselect = [], anomalyIds = [], onDone }) {
  const [f, setF] = useState(blank(preselect));
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setF(blank(preselect)); api.get("/users/assignable").then((r) => setUsers(r.data || [])).catch(() => {}); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (id) => setF((s) => ({ ...s, equipment_ids: s.equipment_ids.includes(id) ? s.equipment_ids.filter((x) => x !== id) : [...s.equipment_ids, id] }));

  const save = async () => {
    if (!f.planned_start) { toast.error("Indiquez la date prévue"); return; }
    if (!f.all_day && !f.start_time) { toast.error("Indiquez l'heure ou choisissez « Journée entière »"); return; }
    if (!f.equipment_ids.length && !anomalyIds.length) { toast.error("Sélectionnez au moins un équipement"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/care/interventions", {
        ...f, site_id: siteId, anomaly_ids: anomalyIds, start_time: f.all_day ? null : f.start_time,
        planned_end: f.planned_end || null, end_time: f.end_time || null, responsible_id: f.responsible_id || null,
      });
      toast.success(`Intervention ${data.reference} planifiée dans SIGNA TRACK (date interne, non publiée au client)`);
      onOpenChange(false); onDone?.(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="intervention-dialog">
        <DialogHeader><DialogTitle>Planifier une intervention</DialogTitle>
          <DialogDescription>L'intervention est créée dans SIGNA TRACK et apparaît dans le planning. La date reste interne tant qu'elle n'est pas publiée au client.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nature *</Label>
              <Select value={f.nature} onValueChange={(v) => setF({ ...f, nature: v })}>
                <SelectTrigger data-testid="int-nature"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(NATURES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Intitulé</Label><Input value={f.title} onChange={set("title")} placeholder="Facultatif" data-testid="int-title" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date prévue *</Label><Input type="date" value={f.planned_start} onChange={set("planned_start")} data-testid="int-date" /></div>
            <div className="space-y-1.5"><Label>Heure</Label><Input type="time" value={f.start_time} disabled={f.all_day} onChange={set("start_time")} data-testid="int-time" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.all_day} onCheckedChange={(v) => setF({ ...f, all_day: !!v })} data-testid="int-allday" />Journée entière</label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fin prévue (facultatif)</Label><Input type="date" value={f.planned_end} onChange={set("planned_end")} data-testid="int-end" /></div>
            <div className="space-y-1.5"><Label>Intervenant</Label>
              <Select value={f.responsible_id || "none"} onValueChange={(v) => setF({ ...f, responsible_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="int-responsible"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Non affecté</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label>Équipements concernés (même site)</Label>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2" data-testid="int-equipment-list">
              {(equipment || []).map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-sm"><Checkbox checked={f.equipment_ids.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`int-eq-${e.id}`} />
                  <span className="font-mono-data text-xs text-slate-400">{e.reference}</span>{e.name}</label>
              ))}
            </div></div>
          <div className="space-y-1.5"><Label>Note de planification (interne)</Label><Textarea value={f.planning_note} onChange={set("planning_note")} data-testid="int-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="int-save">
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Planifier"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
